import React, { useCallback, useEffect, useRef } from 'react';
import ReactHtmlParser from 'react-html-parser';
import { useInView } from 'react-intersection-observer';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { Box, Grid, Link, useMediaQuery } from '@material-ui/core';
import { Carousel } from 'react-responsive-carousel';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CircularProgress from '@material-ui/core/CircularProgress';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import { Mixpanel } from '../mixpanel';
import { attemptProductGlimpseEvent } from '../redux/ProductsListRedux';
import { useAppDispatch, useAppSelector } from '../reduxhooks';
import { sendWebsiteEventMessage } from '../telegrambotevents';
import { FareastButton, FareastButtonGrey } from './OverlayComponentButton';
import { countdownRenderer, countdownRendererImgHalfwidth, determineMediaType } from './ProductDetailShared';
import { VideoComponent } from './PageComponentElementCarousel';
import { postNewProductAnalyticsLog } from '../redux/ProductAnalyticsRedux';
import { getDiscountedPriceAndTimestampInProductSpotlightList } from '../redux/CartRedux';
import Countdown from 'react-countdown';
import { HorizontalInfScrollImg, HorizontalInfScrollImgHalf } from './PageComponentElementHorizontalInfScroll';
import { ArrowRight } from '@material-ui/icons';
import { sendLivestreamMessage, setLivestreamDialog } from '../redux/LivestreamRedux';
import { ProductSpotlight } from 'common-ts/dist/models/Cart';

interface stylesProps {
  image_url: string,
  image_url_hover: string,
  margin: string,
  padding: string,
  width: string
}

const useStyles = ({ image_url, image_url_hover, margin, padding, width }: stylesProps) => makeStyles((theme: Theme) =>
  createStyles({
    ukuranRegulerSizeTextAda: {
      fontSize: "12px",
      fontWeight: 500,
      color: "#232323",
      [theme.breakpoints.down('xs')]: {
        fontSize: "11px",
      }
    },
    ukuranRegulerSizeTextHabis: {
      fontSize: "12px",
      fontWeight: 300,
      color: "#7e7e7e",
      textDecoration: "line-through #7e7e7e solid 1px",
      [theme.breakpoints.down('xs')]: {
        fontSize: "11px",
      }
    },
    video: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    productListItem: {
      width: width ? width : '100%',
      margin: margin,
      padding: padding,
      '&:hover': {
        zIndex: 100, // To ensure that the items come first
      },
    },
    paper: {
      position: "relative", // Needed for child ::before
      overflow: "visible !important", // Needed such that overflows are not clipped
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: '-12px',
        [theme.breakpoints.up('md')]: {
          inset: '-16px',
        },
        backgroundColor: "#ffffff",
        zIndex: -1,
        display: 'none',
      },
      [theme.breakpoints.up('sm')]: {
        '&:hover': {
          '&::before': {
            boxShadow: '0px 0px 18px 8px #c8c8c8',
            display: 'block',
            zIndex: 1,
          },
        },
      }
    },
    cardActionArea: {
      zIndex: 1,
      color: 'aliceblue',
    },
    media: {
      // paddingBottom: '150%', // We use 2 : 3 aspect ratio
      backgroundPosition: 'center top',
      backgroundImage: `url(${image_url})`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      '&:hover': {
        backgroundImage: `url(${image_url_hover})`,
      },
    },
    imageContentContainer: {
      // paddingBottom: '150%', // We use 2 : 3 aspect ratio
      // height: '0px', // This is useful to make an aspect ratio of our liking 
      position: 'relative',
    },
    imageContent: {
      width: '100%',
    },
    hiddenElement: {
      display: 'none',
    },
    itemContent: {
      paddingTop: "14px",
      paddingLeft: "6px",
      paddingRight: "6px",
      paddingBottom: "6px",
      marginBottom: "0px",
    },
    itemHeader: {
      display: "flex",
      flexDirection: "column",
      flexWrap: "nowrap",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: "6px",
    },
    itemHeaderTitleText: {
      fontSize: '1.43vw',
      fontFamily: 'OakesGrotesk, sans-serif',
      fontWeight: 500,
      letterSpacing: '0.03em',
      color: '#232323',
      [theme.breakpoints.down('xs')]: {
        fontSize: '3.3vw',
      },
    },
    itemPriceText: {
      textAlign: 'right',
      fontSize: '1.25vw',
      fontWeight: 300,
      color: '#000000',
      fontFamily: 'Open sans, sans-serif',
      marginTop: '-0.1vw',
      [theme.breakpoints.down('xs')]: {
        fontSize: '3vw',
        marginTop: '0px',
      },
      whiteSpace: 'nowrap',
    },
    itemPriceTextDiscount: {
      textAlign: 'right',
      fontSize: '1vw',
      fontWeight: 500,
      color: 'red',
      fontFamily: 'Open sans, sans-serif',
      marginTop: '-0.1vw',
      marginRight: "1vw",
      [theme.breakpoints.down('xs')]: {
        fontSize: '3vw',
        marginTop: '0px',
        marginRight: "2.4vw",
      },
      whiteSpace: 'nowrap',
    },
    itemPriceTextDiscountStrikeThrough: {
      textAlign: 'right',
      fontSize: '1vw',
      fontWeight: 'lighter',
      color: '#4D4D4D',
      fontFamily: 'Open sans, sans-serif',
      marginTop: '-0.1vw',
      [theme.breakpoints.down('xs')]: {
        fontSize: '3vw',
        marginTop: '0px',
      },
      textDecoration: 'line-through',
      whiteSpace: 'nowrap',
    },
    priceBox: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      width: 'fit-content',
    },
    circularProgressContainer: {
      width: '100%',
      aspectRatio: '2/3', // We use 2 : 3 aspect ratio
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    soldOutOverlay: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'rgba(255,255,255,0.1)',
      zIndex: 1,
    },
    sisaBoxBottomLeft: {
      backgroundColor: "#ff1e1e",
      textAlign: "center",
      position: "absolute",
      bottom: "1vw",
      left: "1vw",
      width: "6vw",
      borderRadius: "1.3vw",
      [theme.breakpoints.down('xs')]: {
        bottom: "2.4vw",
        left: "2.4vw",
        width: "14vw",
        borderRadius: "3vw",
      },
    },
    sisaBoxUpperLeft: {
      backgroundColor: "#ff1e1e",
      textAlign: "center",
      position: "absolute",
      top: "1vw",
      left: "1vw",
      width: "6vw",
      borderRadius: "1.3vw",
      [theme.breakpoints.down('xs')]: {
        top: "3vw",
        left: "2.4vw",
        width: "14vw",
        borderRadius: "3vw",
      },
    },
    sisaText: {
      color: '#ffffff',
      fontSize: "1.3vw",
      paddingTop: "0.3vw",
      [theme.breakpoints.down('xs')]: {
        fontSize: "3vw",
        paddingTop: "0.5vw",
      },
    },
    marketplaceBox: {
      display: 'flex',
      textAlign: "center",
      position: "absolute",
      zIndex: 1,
      top: "-5.5%",
      right: "2%",
      width: "35%",
      [theme.breakpoints.down('xs')]: {
        top: "-8%",
        right: "0%",
        width: "50%",
      },
    },
    marketplaceLogo: {
      width: '104%',
      paddingTop: "0vw",
      [theme.breakpoints.down('xs')]: {
        width: '104%',
        paddingTop: "0vw",
      },
    },
    marketplaceLogoWebOnly: {
      width: '102%',
      paddingTop: "0vw",
      [theme.breakpoints.down('xs')]: {
        width: '102%',
        paddingTop: "0vw",
      },
    },
  }),
);

function AlternatingImg({
  displayImageList,
  startingIndex
}: {
  displayImageList: string[],
  startingIndex?: number
}) {
  const [currentImageIndex, setCurrentImageIndex] = React.useState<number>(startingIndex ?? 0);
  
  const imageSet = displayImageList.filter((item) => {
    // WE FILTER OUT VIDEO
    return determineMediaType(item) === "image";
  }).map((urlImg) => urlImg.replace('.jpg', '').replace('.jpeg', '') + '_1080.jpg');

  useEffect(() => {
    setTimeout(() => {
      setCurrentImageIndex((currentImageIndex + 1) % (imageSet?.length ? imageSet.length : 1));
    }, 1350);
  }, [currentImageIndex]);

  return (
    <img style={{
      width: '100%',
    }} src={imageSet?.[currentImageIndex] ?? ''} alt={"img-hover-rotate"} key={"img-alt"} />
  )
}

export function AlternatingImgCarsl({
  displayImageList,
  startingIndex
}: {
  displayImageList: string[],
  startingIndex?: number
}) {
  // Intersection Observer API
  const { ref, inView, entry } = useInView({
    threshold: 0.1,
    triggerOnce: false,
    rootMargin: '350px',
  });

  const [currentImageIndex, setCurrentImageIndex] = React.useState<number>(startingIndex ?? 0);

  const imageSet = displayImageList.filter((item) => {
    // WE FILTER OUT VIDEO
    return determineMediaType(item) === "image";
  }).map((urlImg) => urlImg.replace('.jpg', '').replace('.jpeg', '') + '_1080.jpg');

  return (
    <div ref={ref} style={{
      width: '100%',
      position: 'relative',
    }}>
      <Carousel
        selectedItem={currentImageIndex}
        autoPlay={inView ? true : false}
        infiniteLoop={true}
        showThumbs={false} 
        showStatus={false}
        showArrows={false}
        showIndicators={false} // WE USE SEPARATE INDICATORS
        onChange={ (index, item) => {
          if (currentImageIndex !== index) {
            setCurrentImageIndex((index) % (imageSet?.length ? imageSet.length : 1));
          }
        }}
        >
        {
          imageSet.map((item, i) => {
            let contentType = determineMediaType(item);

            if (contentType === "image") {
              return (
                <div 
                key={"img-product-" + i }>
                  <img src={item} alt={'car-mini-img'} style={{
                    width: '100%',
                    // marginBottom: '36px', // DEACTIVATED FOR MINI CAROUSEL
                  }} />
              </div>
              );
            } else if (contentType === "video") {
              return (
                <div 
                key={"img-product-" + i }>
                  <VideoComponent 
                    source={item} 
                    videoSoundOn={false}
                  />
                </div>
              );
            } else {
              return (
                <div 
                key={"img-product-" + i }>
                  <img src={item} alt={'car-mini-img'} style={{
                    width: '100%',
                    // marginBottom: '36px', // DEACTIVATED FOR MINI CAROUSEL
                  }} />
                </div>
              );
            }
          }).filter(Boolean)
        }
      </Carousel>
      <Box style={{
        position: 'absolute',
        bottom: '9px',
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
      }}>
        <Box style={{
          display: 'flex',
          backgroundColor: '#ffffff',
          borderRadius: '30px',
          padding: '4px',
        }}>
          {
            imageSet.map((obj, index) => {
              return (
                <div
                style={{
                  background: currentImageIndex === index ? '#000' : '#999',
                  width: 7,
                  height: 7,
                  margin: '0px 2px',
                  borderRadius: '50%',
                }}
                />
              )
            })
          }
        </Box>
      </Box>
    </div>
  )
}

export function MultipleImgCarsl({
  displayImageList,
  startingIndex = 0,
  splitInto,
  showIndicators = false,
  autoSlide = true,
}: {
  displayImageList: string[],
  startingIndex?: number
  splitInto?: number
  showIndicators?: boolean
  autoSlide?: boolean
}) {
  // Intersection Observer API
  const { ref, inView, entry } = useInView({
    threshold: 0.1,
    triggerOnce: false,
    rootMargin: '350px',
  });
  const [currentImageIndex, setCurrentImageIndex] = React.useState<number>(0);

  const imageSet = displayImageList.filter((item) => {
    // WE FILTER OUT VIDEO
    return determineMediaType(item) === "image";
  }).map((urlImg) => urlImg.replace('.jpg', '').replace('.jpeg', '') + '_360.jpg');
  const splitIntoSet = splitInto ?? 2;
  const widthSet = 
    splitIntoSet ===  2 ? '49%' : 
    splitIntoSet ===  3 ? '32%' : 
    splitIntoSet ===  4 ? '24%' : 
    '100%';

  const imageSetStartingImages = [];
  for (var i = startingIndex;  i < startingIndex + imageSet.length; i = i + splitIntoSet) {
    imageSetStartingImages.push({
      index: i % imageSet.length,
      contentImage: imageSet[i % imageSet.length]
    })
  }

  return (
    <div ref={ref} style={{
      width: '100%',
      position: 'relative',
    }}>
      <Carousel
        selectedItem={currentImageIndex}
        autoPlay={inView ? autoSlide : false}
        infiniteLoop={true}
        interval={3500}
        showThumbs={false} 
        showStatus={false}
        showArrows={false}
        showIndicators={showIndicators}
        renderIndicator={(onClickHandler, isSelected, index, label) => {
          if (isSelected) {
            return (
              <li
                style={{
                  display: 'inline-block',
                  background: '#000',
                  width: 8,
                  height: 8,
                  margin: '0px 4px',
                  borderRadius: '50%',
                }}
                aria-label={`Selected: ${label} ${index + 1}`}
                title={`Selected: ${label} ${index + 1}`}
              >

              </li>
            );
          }
          return (
            <li
              style={{
                display: 'inline-block',
                background: '#999',
                width: 8,
                height: 8,
                margin: '0px 4px',
                borderRadius: '50%',
              }}
              onClick={onClickHandler}
              onKeyDown={onClickHandler}
              value={index}
              key={index}
              role="button"
              tabIndex={0}
              title={`${label} ${index + 1}`}
              aria-label={`${label} ${index + 1}`}
            >
            </li>
          );
        }}
        onChange={ (index, item) => {
          if (currentImageIndex !== index) {
            setCurrentImageIndex((index) % (displayImageList?.length ? displayImageList.length : 1));
          }
        }}
        >
        {
          imageSetStartingImages.map((item, i) => {

            return (
              <div 
              key={"img-product-" + i } style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: showIndicators ? '32px' : '0px'
              }}>
                <img src={item.contentImage} alt={'car-mini-img'} style={{
                  width: widthSet,
                }} />
                {
                  splitIntoSet >= 2 ?
                  <img src={imageSet[(item.index + 1) % (imageSet.length ? imageSet.length : 1)]} alt={'car-mini-img'} style={{
                    width: widthSet,
                  }} />:
                  null
                }
                {
                  splitIntoSet >= 3 ?
                  <img src={imageSet[(item.index + 2) % (imageSet.length ? imageSet.length : 1)]} alt={'car-mini-img'} style={{
                    width: widthSet,
                  }} />:
                  null
                }
                {
                  splitIntoSet >= 4 ?
                  <img src={imageSet[(item.index + 3) % (imageSet.length ? imageSet.length : 1)]} alt={'car-mini-img'} style={{
                    width: widthSet,
                  }} />:
                  null
                }
              </div>
            );
          }).filter(Boolean)
        }
      </Carousel>
    </div>
  )
}

export function LiveLogoMobile({styleAdd={}}: {styleAdd?: any}) {
  return (
    <Box style={{
      borderRadius: '40px', 
      backgroundColor: '#232323', 
      display: 'flex', 
      alignItems: 'center', 
      margin: '0px 0px 0px 5px',
      padding: '5px 10px 5px 8px',
      ...styleAdd,
    }}>
      <div style={{width: '8px', height: '8px', borderRadius: '8px', 
        backgroundColor: 'rgb(237, 21, 21)',
        animation: 'blinkAnimation 1.4s steps(5, start) infinite'}}></div>
      <div></div><Typography style={{fontSize: '10px', color: '#ffffff', lineHeight: '10px', margin: '1px 0px -1px 5px', letterSpacing: '0.4px',}}>LIVE</Typography>
    </Box>
  );
}
 
interface PageProductItemProps {
  product_id: string,
  image_url: string, // If this is equal to 'animate-blink-{startIndex}' or 'animate-carsl-{startIndex}', we display differently
  image_url_hover: string,
  title: string,
  originalPrice: number,
  discountedPrice: number,
  image_additional_url?: string,
  isSoldOut?: boolean,
  isComingAgainSoon?: boolean,
  product_url: string,
  margin?: string,
  padding?: string,
  width?: string,
  marketplaceLinkData?: {
    [key: string]: string;
  }
}

interface MarketplaceLinkData {
  [key: string]: string;
}

export const marketplaceLogoDictionary = {
  'shopee': process.env.PUBLIC_URL + '/assets/icon-shopee.png',
  'tokopedia': process.env.PUBLIC_URL + '/assets/icon-tokopedia.png',
  'tiktok': process.env.PUBLIC_URL + '/assets/icon-tiktok.png',
}

export default function PageElementProduct({ product_id, image_url, image_url_hover, title, originalPrice, discountedPrice: discountedPriceDef, isSoldOut,
  isComingAgainSoon, product_url, margin, padding, marketplaceLinkData, image_additional_url, width }: PageProductItemProps) {
  width = width ? width : '100%';
  margin = margin ? margin : '0px 0px 0px 0px';
  padding = padding ? padding : '30px 9px 9px 9px';
  const productSizeAvailabilityDict = useAppSelector(state => state.productsList.productSizeAvailabilityDict);
  const productsList = useAppSelector((state) => state.productsList);

  const streamIsOn = useAppSelector((state) => state.livestreamState.streamIsOn);

  // image_url = 'animate-carsl-1';
  // image_url = 'animate-blink-1';
  // image_additional_url = 'carsl-thr-2';
  // image_additional_url = 'inf-scroll';

  const coupon = useAppSelector(state => state.cart.coupon);
  const productSpotlightList = useAppSelector(state => state.cart.productSpotlightList ?? []);

  let combinedProductSpotlightList: ProductSpotlight[] = productSpotlightList.concat(coupon?.couponProductSpotlightList?.map((prodObj) => ({
    ...prodObj,
    timestampStart: '1999-10-10T00:00:00.000Z',
    timestampLimit: coupon?.expirationDateTime ?? '1999-10-10T00:00:00.000Z', 
    isMainSpotlight: false,
    alternativeSource: 'COUPON'
  })) ?? []);
  let activeSpotlightPromo = coupon?.couponBlastingEffects?.length ? {
    discountedPrice: Math.round((1 - coupon?.couponBlastingEffects?.[0].effectValue) * (originalPrice)),
    timestampStart: '1999-10-10T00:00:00.000Z',
    timestampLimit: coupon?.expirationDateTime ?? '1999-10-10T00:00:00.000Z', 
    isMainSpotlight: false,
    alternativeSource: 'COUPON'
  } : getDiscountedPriceAndTimestampInProductSpotlightList(combinedProductSpotlightList, product_id);

  let spotlightDiscPrice = activeSpotlightPromo.discountedPrice;
  let discountedPrice = spotlightDiscPrice !== -1 ? spotlightDiscPrice : discountedPriceDef;

  let productSpotlightTime = activeSpotlightPromo.timestampLimit;
  let spotlightSource = activeSpotlightPromo?.alternativeSource ?? "FLASHSALE";

  const classes = useStyles({ image_url, image_url_hover, margin, padding, width })();
  const dispatch = useAppDispatch();

  const [lastRenderTime, setLastRenderTime] = React.useState<string>('');

  // DETERMINE MEDIA TYPE
  let mediaType = image_url.startsWith('animate-blink-') ? 'animate-blink' : 
    image_url.startsWith('animate-carsl-') ? 'animate-carsl' : 
    determineMediaType(image_url);

  const numberOfItemsRemaining = productSizeAvailabilityDict?.[product_id]?.numberOfItemsRemaining ?? 0;
  const ukuranPlusAda = productSizeAvailabilityDict?.[product_id]?.availabilityArray?.[9];

  const productDetail = productsList.productsDictionary?.[product_id];

  // BUNDLING ADDITION: OVERWRITES ANYTHING ELSE
  let bundlingMarker = false;
  if (productDetail.bundlingData) {
    discountedPrice = -1;
    originalPrice = 
      productDetail.bundlingData.bundlingPriceDisplayIndex === "1" ? 
      productDetail.bundlingData.bundlingPrice1 :
      productDetail.bundlingData.bundlingPriceDisplayIndex === "2" ? 
      productDetail.bundlingData.bundlingPrice2 :
      productDetail.bundlingData.bundlingPriceDisplayIndex === "3" ? 
      productDetail.bundlingData.bundlingPrice3 :
      originalPrice;
    if (productDetail.bundlingData.bundlingPriceDisplayIndex === "2" || 
      productDetail.bundlingData.bundlingPriceDisplayIndex === "3") {
      bundlingMarker = true;
    }
  }

  isSoldOut = isSoldOut || !numberOfItemsRemaining;

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
  const isMdOrUp = useMediaQuery(theme.breakpoints.up("sm"));

  // We set additional image to carousel three for Desktop
  if (isMdOrUp) {
    image_additional_url = 'carsl-thr-2';
  }

  const [isHovered, setIsHovered] = React.useState(false);

  const [imgHover, setImgHover] = React.useState<string>('');
  const [imgNotHover, setImgNotHover] = React.useState<string>('');

  const [imgHoverContainer, setImgHoverContainer] = React.useState<string>('');
  const [imgNotHoverContainer, setImgNotHoverContainer] = React.useState<string>('');

  // Intersection Observer API for lazy loading
  const { ref, inView, entry } = useInView({
    threshold: [0.02, 0.05, 0.1, 0.2],
    triggerOnce: true,
    rootMargin: '400px',
  });

  // Intersection Observer API for viewing event
  const { ref: ref2, inView: inView2, entry: entry2 } = useInView({
    threshold: 0.7,
    triggerOnce: false,
    rootMargin: '10px',
  });

  useEffect(() => {
    if (inView && isMdOrUp) {
      const imgHoverNew = new Image();
      imgHoverNew.onload = () => {
        setImgHoverContainer(imgHoverNew.src);
      }
      imgHoverNew.src = image_url_hover;

      const imgNotHoverNew = new Image();
      imgNotHoverNew.onload = () => {
        setImgNotHoverContainer(imgNotHoverNew.src);
      }
      imgNotHoverNew.src = image_url;
    }
  }, [image_url, image_url_hover, inView]);

  useEffect(() => {
    if (inView2) {
      dispatch(attemptProductGlimpseEvent(product_id));
      dispatch(postNewProductAnalyticsLog({event: 'VIEW', productId: product_id, isMobileView: !isDesktop }))
    }
  }, [inView2]);

  // Use `useCallback` so we don't recreate the function on each render
  const setRefs = useCallback(
    (node) => {
      ref(node);
      ref2(node);
    },
    [ref, ref2],
  );

  if (imgHoverContainer !== "" && imgHoverContainer === image_url_hover && imgHoverContainer !== imgHover) {
    setImgHover(imgHoverContainer);
  }
  if (imgNotHoverContainer !== "" && imgNotHoverContainer === image_url && imgNotHoverContainer !== imgNotHover) {
    setImgNotHover(imgNotHoverContainer);
  }

  if (!isMdOrUp) {
    return (
        <div className={classes.productListItem} ref={setRefs}
          onClick={() => {

            sendWebsiteEventMessage("Ada yang buka product " + title + " dari x" + window.location.href);

            Mixpanel.track('navigateRoute', {
              'routePath': product_url,
              'routeSource': 'pageProductListItem',
            });
            Mixpanel.track('productClickToProductDetailPage', {
              'productId': "productId-" + product_id,
              'productUrl': product_url,
              'routeSource': 'pageProductListItem',
            });
            console.log("Tracking mixpanel productClickToProductDetailPage for product " + product_id);
          }}>
            <Link component={RouterLink} to={product_url} underline="none" style={{
              WebkitTapHighlightColor: 'transparent',
            }}>
              <div className={classes.imageContentContainer}>
                {/* {
                  (marketplaceLinkData && Object.keys(marketplaceLinkData as any).length > 0) ?
                  <Box className={classes.marketplaceBox}>
                    <img
                      src={process.env.PUBLIC_URL + '/assets/icon-product-mktplc.png'}
                      className={classes.marketplaceLogo}
                      alt={`mktplc logo`}
                    />
                  </Box> :
                  <Box className={classes.marketplaceBox}>
                    <img
                      src={process.env.PUBLIC_URL + '/assets/icon-product-web-only.png'}
                      className={classes.marketplaceLogoWebOnly}
                      alt={`mktplc logo`}
                    />
                  </Box> 
                } */}
                {
                  isSoldOut ?
                  <Box className={classes.soldOutOverlay}>
                    <FareastButton
                      buttonMarginLeftRight='20px'
                      buttonMarginTopBottom='20px'>
                      <b>SOLD OUT</b>
                    </FareastButton>
                  </Box> :
                  isComingAgainSoon ?
                  <Box className={classes.soldOutOverlay}>
                    <FareastButtonGrey
                      buttonMarginLeftRight='20px'
                      buttonMarginTopBottom='20px'>
                      <b>COMING BACK SOON</b>
                    </FareastButtonGrey>
                  </Box> :
                  null
                }
                {
                  mediaType === 'video' && image_url ? 
                  (
                    <div key={"full-img-product-" + product_id }>
                      <VideoComponent 
                        source={image_url} 
                        videoHasSound={false} // All element product video has sound disabled
                      />
                    </div>
                  ) :
                  mediaType === 'animate-blink' && image_url ? 
                  (
                    <AlternatingImg startingIndex={Number(image_url.substring(14, 15))} displayImageList={productDetail?.productImages ?? []} />
                  ) : 
                  mediaType === 'animate-carsl' && image_url ? 
                  (
                    <AlternatingImgCarsl startingIndex={Number(image_url.substring(14, 15))} displayImageList={productDetail?.productImages ?? []} />
                  ) : 
                  (
                    <img className={classes.imageContent} src={image_url.replace('.jpg', '').replace('.jpeg', '') + '_1080.jpg'} alt={"img-hover-" + product_id} key={"img-hover-" + product_id} />
                  )
                }
                {
                  !isSoldOut && numberOfItemsRemaining && numberOfItemsRemaining < 14 ?
                    <Box className={mediaType === 'animate-carsl' ? classes.sisaBoxUpperLeft : classes.sisaBoxBottomLeft}>
                      <Typography className={classes.sisaText}>
                        Sisa {numberOfItemsRemaining}
                      </Typography>
                    </Box> :
                    null
                }
              </div>
            </Link>
            <Link component={RouterLink} to={product_url} underline="none">
              {
                image_additional_url?.startsWith('carsl-two-') ?
                <MultipleImgCarsl 
                  startingIndex={Number(image_additional_url.substring(10, 11))}
                  displayImageList={productDetail?.productImages ?? []}
                  splitInto={2}
                />:
                image_additional_url?.startsWith('carsl-thr-') ?
                <MultipleImgCarsl
                  startingIndex={Number(image_additional_url.substring(10, 11))}
                  displayImageList={productDetail?.productImages ?? []}
                  splitInto={3}
                />:
                image_additional_url?.startsWith('carsl-for-') ?
                <MultipleImgCarsl
                  startingIndex={Number(image_additional_url.substring(10, 11))}
                  displayImageList={productDetail?.productImages ?? []}
                  splitInto={4}
                />:
                image_additional_url === 'inf-scroll' ?
                <HorizontalInfScrollImg 
                  displayImageList={productDetail?.productImages ?? []}
                />:
                image_additional_url === 'inf-scroll-half' ?
                <HorizontalInfScrollImgHalf
                  displayImageList={productDetail?.productImages ?? []}
                />:
                null
              }
            </Link>
            {
              (new Date().toISOString() < productSpotlightTime) &&
              <Box style={{margin: '13px 0px -2px 5px'}}>
                <Countdown
                  date={productSpotlightTime}
                  renderer={spotlightSource === "COUPON" && coupon?.couponHalfWidthCountdownMedia ? countdownRendererImgHalfwidth(coupon.couponHalfWidthCountdownMedia) : countdownRenderer}
                  onComplete={(timeDelta, completedOnStart) => {
                    setTimeout(() => {
                      setLastRenderTime(new Date().toISOString())
                    }, 400)
                  }}
                />
              </Box>
            } 
            {
              streamIsOn &&
              <Box style={{ 
                width: '100%',
                borderRadius: '300px',
                border: '1px solid rgb(200, 200, 200)',
                margin: '9px 0px 0px',
                padding: '2px 0px',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'relative',
              }} onClick={() => {
                dispatch(sendLivestreamMessage("Meminta produk: " + title));
                dispatch(setLivestreamDialog('LIVE'));
              }}>
                <LiveLogoMobile />
                <Typography style={{padding: '0.5vw 0px 0.1vw 0px', fontSize: '2.4vw', letterSpacing: '0.04vw'}}>LIHAT PRODUK DI LIVE</Typography>
                <ArrowRight style={{marginLeft: '-8px', paddingBottom: '2px'}}/>
              </Box>
            } 
            {
              (window.location?.href?.includes("/plus-size")) &&
              <Grid container style={{
                padding: '3px 9px',
                marginTop: '6px',
                marginBottom: '4px',
                border: '1px solid rgb(194,194,194)',
                borderRadius: '30px'
              }}>
                <Grid item xs={3} style={{display: 'flex', alignItems: 'center'}}>
                  <Typography style={{
                    fontSize: '8px',
                    padding: '2px 6px 0px',
                    marginRight: '7px',
                    border: '1px solid rgb(6, 37, 158)',
                    borderRadius: '40px',
                    color: 'rgb(6, 37, 158)',
                    height: 'fit-content'
                  }}>PLUS</Typography>
                </Grid>
                <Grid item xs={9} style={{
                  display: 'flex',
                  justifyContent: "space-between",
                  paddingTop: '2px',
                }}>
                  {
                    // WE DON'T SHOW 8XL OPTION HERE. TOO TIGHT.
                    ["3XL", "4XL", "5XL", "6XL", "7XL"].map((value, index) => {
                      const ukuranAda = productSizeAvailabilityDict?.[product_id]?.availabilityArray?.[index + 7];
                      return (
                        <Typography className={ ukuranAda ? classes.ukuranRegulerSizeTextAda : classes.ukuranRegulerSizeTextHabis }>
                          {value}
                        </Typography>
                      );
                    })
                  }
                </Grid>
              </Grid>
            } 
            <Box className={classes.itemContent}>
              <Link component={RouterLink} to={product_url} underline="none" style={{
                WebkitTapHighlightColor: 'transparent',
              }}>
              <Box className={classes.itemHeader}>
                <Typography className={classes.itemHeaderTitleText}>
                  {ReactHtmlParser(title)}
                </Typography>
                {
                  discountedPrice === -1 || isSoldOut === true || isComingAgainSoon === true ?
                    <Box className={classes.priceBox}>
                      <Typography className={classes.itemPriceText}>
                        {("Rp " + originalPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","))}
                      </Typography>
                      {
                        bundlingMarker ? 
                        <Typography style={{
                          marginLeft: '3px',
                          fontSize: '1.9vw',
                          fontWeight: 600,
                          padding: '0.6vw 1.35vw 0.06vw',
                          border: '1px solid rgb(224, 224, 224)',
                          borderRadius: '150px',
                          color: '#121212',
                          height: 'fit-content',
                          alignSelf: 'anchor-center',
                          letterSpacing: '0.08vw',
                        }}>
                          BUNDLING
                        </Typography>: 
                        null
                      }
                    </Box> :
                    <Box className={classes.priceBox}>
                      <Typography className={classes.itemPriceTextDiscount}>
                        {("Rp " + discountedPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","))}
                      </Typography>
                      <Typography className={classes.itemPriceTextDiscountStrikeThrough}>
                        {("Rp " + originalPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","))}
                      </Typography>
                    </Box>
                }
              </Box>
              </Link>
            </Box>
        </div>
    );
  }

  return (
    <div className={classes.productListItem} ref={setRefs}>
      <Card elevation={0} square={true} className={classes.paper}>
        <CardActionArea component={RouterLink} to={product_url} disableRipple
          onClick={() => {

            sendWebsiteEventMessage("Ada yang buka product " + title + " dari x" + window.location.href);

            (window as any).dataLayer.push({ 'event': 'navigateRoute', 'routePath': product_url, 'routeSource': 'pageProductListItem' });
            Mixpanel.track('navigateRoute', {
              'routePath': product_url,
              'routeSource': 'pageProductListItem',
            });
            Mixpanel.track('productClickToProductDetailPage', {
              'productId': "productId-" + product_id,
              'productUrl': product_url,
              'routeSource': 'pageProductListItem',
            });
            console.log("Tracking mixpanel productClickToProductDetailPage for product " + product_id);
          }} className={classes.cardActionArea}>
          {
            (!imgHover || !imgNotHover || imgHover !== image_url_hover || imgNotHover !== image_url) && (mediaType !== 'video')?
              <div className={classes.circularProgressContainer}>
                <CircularProgress />
              </div> :
              <div className={classes.imageContentContainer}
                onMouseEnter={() => setIsHovered(isMdOrUp)}
                onMouseLeave={() => setIsHovered(false)}>
                {/* {
                  (marketplaceLinkData && Object.keys(marketplaceLinkData as any).length > 0) ?
                  <Box className={classes.marketplaceBox}>
                    <img
                      src={process.env.PUBLIC_URL + '/assets/icon-product-mktplc.png'}
                      className={classes.marketplaceLogo}
                      alt={`mktplc logo`}
                    />
                  </Box> :
                  <Box className={classes.marketplaceBox}>
                    <img
                      src={process.env.PUBLIC_URL + '/assets/icon-product-web-only.png'}
                      className={classes.marketplaceLogoWebOnly}
                      alt={`mktplc logo`}
                    />
                  </Box> 
                } */}
                {
                  isSoldOut ?
                    <Box className={classes.soldOutOverlay}>
                      <FareastButton
                        buttonMarginLeftRight='20px'
                        buttonMarginTopBottom='20px'>
                        <b>SOLD OUT</b>
                      </FareastButton>
                    </Box> :
                    isComingAgainSoon ?
                      <Box className={classes.soldOutOverlay}>
                        <FareastButtonGrey
                          buttonMarginLeftRight='20px'
                          buttonMarginTopBottom='20px'>
                          <b>COMING BACK SOON</b>
                        </FareastButtonGrey>
                      </Box> :
                      null
                }
                {
                  isHovered && false ?
                  <img className={classes.imageContent} src={imgHover} alt={"img-" + product_id} key={"img-" + product_id} /> : (
                  <>
                  {
                    mediaType === 'video' && image_url ? 
                    (
                      <div key={"full-img-product-" + product_id }>
                        <VideoComponent 
                          source={image_url} 
                          videoHasSound={false} // All element product video has sound disabled
                        />
                      </div>
                    ) :
                    mediaType === 'animate-blink' && image_url ? 
                    (
                      <AlternatingImg startingIndex={Number(image_url.substring(14, 15))} displayImageList={productDetail?.productImages ?? []} />
                    ) : 
                    mediaType === 'animate-carsl' && image_url ? 
                    (
                      <AlternatingImgCarsl startingIndex={Number(image_url.substring(14, 15))} displayImageList={productDetail?.productImages ?? []} />
                    ) : 
                    (
                      <img className={classes.imageContent} src={imgNotHover.replace('.jpg', '').replace('.jpeg', '') + '_1080.jpg'} alt={"img-hover-" + product_id} key={"img-hover-" + product_id} />
                    )
                  }
                  </>)
                }
                {
                  !isSoldOut && numberOfItemsRemaining && numberOfItemsRemaining < 14 ?
                    <Box className={mediaType === 'animate-carsl' ? classes.sisaBoxUpperLeft : classes.sisaBoxBottomLeft}>
                      <Typography className={classes.sisaText}>
                        Sisa {numberOfItemsRemaining}
                      </Typography>
                    </Box> :
                    null
                }
              </div>
          }
          <Link component={RouterLink} to={product_url} underline="none">
            {
              image_additional_url?.startsWith('carsl-two-') ?
              <MultipleImgCarsl 
                startingIndex={Number(image_additional_url.substring(10, 11))}
                displayImageList={productDetail?.productImages ?? []}
                splitInto={2}
              />:
              image_additional_url?.startsWith('carsl-thr-') ?
              <MultipleImgCarsl
                startingIndex={Number(image_additional_url.substring(10, 11))}
                displayImageList={productDetail?.productImages ?? []}
                splitInto={3}
              />:
              image_additional_url?.startsWith('carsl-for-') ?
              <MultipleImgCarsl
                startingIndex={Number(image_additional_url.substring(10, 11))}
                displayImageList={productDetail?.productImages ?? []}
                splitInto={4}
              />:
              image_additional_url === 'inf-scroll' ?
              <HorizontalInfScrollImg 
                displayImageList={productDetail?.productImages ?? []}
              />:
              image_additional_url === 'inf-scroll-half' ?
              <HorizontalInfScrollImgHalf
                displayImageList={productDetail?.productImages ?? []}
              />:
              null
            }
          </Link>
          {
            (new Date().toISOString() < productSpotlightTime) &&
            <Box style={{margin: '15px 0px 0px 5px'}}>
              <Countdown
                date={productSpotlightTime}
                renderer={spotlightSource === "COUPON" && coupon?.couponHalfWidthCountdownMedia ? countdownRendererImgHalfwidth(coupon.couponHalfWidthCountdownMedia) : countdownRenderer}
                onComplete={(timeDelta, completedOnStart) => {
                  setTimeout(() => {
                    setLastRenderTime(new Date().toISOString())
                  }, 400)
                }}
              />
            </Box>
          } 
          {
            streamIsOn &&
            <Box style={{ 
              width: '100%',
              borderRadius: '300px',
              border: '1px solid rgb(200, 200, 200)',
              margin: '9px 0px 0px',
              padding: '2px 0px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative', 
              color: '#121212'
            }} onClick={() => {
              dispatch(sendLivestreamMessage("Meminta produk: " + title));
              dispatch(setLivestreamDialog('LIVE'));
            }}>
              <LiveLogoMobile />
              <Typography style={{padding: '2.5px 0px 0.5px 0px', fontSize: '12px', letterSpacing: '0.04vw'}}>LIHAT PRODUK DI LIVE</Typography>
              <ArrowRight style={{marginLeft: '-8px', paddingBottom: '2px'}}/>
            </Box>
          } 
          {
            (window.location?.href?.includes("/plus-size")) &&
            <Grid container style={{
              padding: '3px 9px',
              marginTop: '6px',
              marginBottom: '4px',
              border: '1px solid rgb(194,194,194)',
              borderRadius: '30px'
            }}>
              <Grid item xs={3} style={{display: 'flex', alignItems: 'center'}}>
                <Typography style={{
                  fontSize: '8px',
                  padding: '2px 6px 0px',
                  marginRight: '7px',
                  border: '1px solid rgb(6, 37, 158)',
                  borderRadius: '40px',
                  color: 'rgb(6, 37, 158)',
                  height: 'fit-content'
                }}>PLUS</Typography>
              </Grid>
              <Grid item xs={9} style={{
                display: 'flex',
                justifyContent: "space-between",
                paddingTop: '2px',
              }}>
                {
                  // WE DON'T SHOW 8XL OPTION HERE. TOO TIGHT.
                  ["3XL", "4XL", "5XL", "6XL", "7XL"].map((value, index) => {
                    const ukuranAda = productSizeAvailabilityDict?.[product_id]?.availabilityArray?.[index + 7];
                    return (
                      <Typography className={ ukuranAda ? classes.ukuranRegulerSizeTextAda : classes.ukuranRegulerSizeTextHabis }>
                        {value}
                      </Typography>
                    );
                  })
                }
              </Grid>
            </Grid>
          } 
          <Box className={classes.itemContent}>
            <Link component={RouterLink} to={product_url} underline="none">
              <Box className={classes.itemHeader}>
                <Typography className={classes.itemHeaderTitleText}>
                  {ReactHtmlParser(title)}
                </Typography>
                {
                  discountedPrice === -1 || isSoldOut === true || isComingAgainSoon === true ?
                    <Box className={classes.priceBox}>
                      <Typography className={classes.itemPriceText}>
                        {("Rp " + originalPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","))}
                      </Typography>
                      {
                        bundlingMarker ? 
                        <Typography style={{
                          marginLeft: '5px',
                          fontSize: '0.55vw',
                          fontWeight: 600,
                          padding: '0.2vw 0.45vw 0.02vw',
                          border: '1px solid rgb(224, 224, 224)',
                          borderRadius: '150px',
                          color: '#121212',
                          height: 'fit-content',
                          alignSelf: 'anchor-center',
                          letterSpacing: '0.02vw',
                        }}>
                          BUNDLING
                        </Typography>: 
                        null
                      }
                    </Box> :
                    <Box className={classes.priceBox}>
                      <Typography className={classes.itemPriceTextDiscount}>
                        {("Rp " + discountedPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","))}
                      </Typography>
                      <Typography className={classes.itemPriceTextDiscountStrikeThrough}>
                        {("Rp " + originalPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","))}
                      </Typography>
                    </Box>
                }
              </Box>
            </Link>
          </Box>
        </CardActionArea>
      </Card>
    </div>
  );
  
}